/**
 * NotificationBell Component
 * 
 * Shows a bell icon with unread count badge.
 * Click opens dropdown with recent notifications.
 */
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationsAPI } from '../lib/api';
import { useAppBadge } from '../hooks/useAppBadge';
import { usePendingTasks } from '../contexts/PendingTasksContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  Bell,
  Check,
  CheckCheck,
  X,
  UserPlus,
  FileText,
  Calendar,
  Clock,
  Trash2,
  Settings,
  ExternalLink,
} from 'lucide-react';

const NOTIFICATION_ICONS = {
  workflow_task: UserPlus,
  change_request_new: FileText,
  change_request_approved: FileText,
  change_request_rejected: FileText,
  vacation_request_new: Calendar,
  vacation_approved: Calendar,
  vacation_rejected: Calendar,
  vacation_cancelled: Calendar,
  correction_request_new: Clock,
  correction_approved: Clock,
  correction_rejected: Clock,
};

const NOTIFICATION_COLORS = {
  workflow_task: 'bg-blue-100 text-blue-600',
  change_request_new: 'bg-purple-100 text-purple-600',
  change_request_approved: 'bg-green-100 text-green-600',
  change_request_rejected: 'bg-red-100 text-red-600',
  vacation_request_new: 'bg-amber-100 text-amber-600',
  vacation_approved: 'bg-green-100 text-green-600',
  vacation_rejected: 'bg-red-100 text-red-600',
  vacation_cancelled: 'bg-red-100 text-red-600',
  correction_request_new: 'bg-orange-100 text-orange-600',
  correction_approved: 'bg-green-100 text-green-600',
  correction_rejected: 'bg-red-100 text-red-600',
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = parseISO(dateStr);
    return formatDistanceToNow(date, { addSuffix: true, locale: de });
  } catch {
    return dateStr;
  }
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const { total: pendingTotal, unreadNotifications: unreadCount, reload: reloadPending } = usePendingTasks();

  // App-Icon-Badge (PWA), Tab-Titel und Favicon synchron halten.
  // Summe aus ungelesenen Notifications + offenen Freigaben.
  useAppBadge((unreadCount || 0) + (pendingTotal || 0));

  // Liste der Benachrichtigungen wird LAZY geladen – erst beim Öffnen der Glocke.
  // Der ungelesene Zähler kommt aus dem PendingTasksContext (ein gemeinsamer
  // Request pro Seite statt zusätzlicher Calls je Komponente).
  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const notifRes = await notificationsAPI.getAll({ limit: 20 });
      setNotifications(notifRes.data);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleOpenChange = (next) => {
    setOpen(next);
    if (next) loadNotifications();
  };

  const handleMarkAsRead = async (id, e) => {
    e.stopPropagation();
    try {
      await notificationsAPI.markAsRead(id);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      );
      reloadPending();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      reloadPending();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await notificationsAPI.delete(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      reloadPending();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read if not already
    if (!notification.is_read) {
      try {
        await notificationsAPI.markAsRead(notification.id);
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
        );
        reloadPending();
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }

    // Navigate to link if present
    if (notification.link) {
      setOpen(false);
      navigate(notification.link);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-medium">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96" data-testid="notification-dropdown">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold">Benachrichtigungen</h3>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={handleMarkAllAsRead}
              >
                <CheckCheck className="h-3 w-3 mr-1" />
                Alle gelesen
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => {
                setOpen(false);
                navigate('/mein-profil?tab=benachrichtigungen');
              }}
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Notifications List */}
        <ScrollArea className="h-[400px]">
          {loading && notifications.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">
              Wird geladen …
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Bell className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p>Keine Benachrichtigungen</p>
            </div>
          ) : (
            <div className="py-1">
              {notifications.map((notification) => {
                const Icon = NOTIFICATION_ICONS[notification.type] || Bell;
                const colorClass = NOTIFICATION_COLORS[notification.type] || 'bg-gray-100 text-gray-600';
                
                return (
                  <div
                    key={notification.id}
                    className={`
                      px-4 py-3 hover:bg-muted/50 cursor-pointer border-b border-border/50 last:border-b-0
                      ${!notification.is_read ? 'bg-blue-50/50' : ''}
                    `}
                    onClick={() => handleNotificationClick(notification)}
                    data-testid={`notification-${notification.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg flex-shrink-0 ${colorClass}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <p className={`text-sm font-medium ${!notification.is_read ? 'text-foreground' : 'text-muted-foreground'}`}>
                              {notification.title}
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                              {notification.message}
                            </p>
                            <p className="text-xs text-muted-foreground/70 mt-1">
                              {formatTime(notification.created_at)}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {!notification.is_read && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 opacity-0 group-hover:opacity-100"
                                onClick={(e) => handleMarkAsRead(notification.id, e)}
                              >
                                <Check className="h-3 w-3" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-muted-foreground hover:text-red-500"
                              onClick={(e) => handleDelete(notification.id, e)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
